import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Badge } from "@/components/ui/baseUi/badge"
import { Button } from "@/components/ui/baseUi/button"
import {
  Carousel,
  CarouselContent,
  CarouselIndicator,
  CarouselIndicators,
  CarouselItem,
} from "@/components/ui/baseUi/carousel"
import { Text } from "@/components/ui/baseUi/text"
import { useCurrentCoachIfExists } from "@/modules/coach/hooks/useProfessionalCoach"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { Path } from "@/modules/navigation/routes"
import { LogoHustleUpSmall } from "@images/logoHustleUpSmall"
import { LogoHustleUpText } from "@images/logoHustleUpText"
import {
  Banknote,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MegaphoneIcon,
  TagsIcon,
  UserRoundPenIcon,
  X,
} from "lucide-react"
import { useCallback, useEffect, useEffectEvent, useRef, useState } from "react"
import { useNavigate } from "react-router"

type FeatureKey = "offers" | "planning" | "athletes" | "business" | "visibility"

const FEATURE_CARDS: { key: FeatureKey; icon: React.ReactNode }[] = [
  { key: "offers", icon: <TagsIcon className="size-6 text-textOnSurface-primary-subtle" /> },
  { key: "planning", icon: <CalendarDaysIcon className="size-6 text-textOnSurface-primary-subtle" /> },
  { key: "athletes", icon: <UserRoundPenIcon className="size-6 text-textOnSurface-primary-subtle" /> },
  { key: "business", icon: <Banknote className="size-6 text-textOnSurface-primary-subtle" /> },
  { key: "visibility", icon: <MegaphoneIcon className="size-6 text-textOnSurface-primary-subtle" /> },
]

export function CoachOnboardingPage() {
  const navigate = useNavigate()
  const { setTheme } = useTheme()
  const scrollRef = useRef<HTMLDivElement>(null)
  const { currentCoach } = useCurrentCoachIfExists()
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)

  const applyCoachTheme = useEffectEvent(() => {
    setTheme(Theme.CoachLight)
  })

  useEffect(() => {
    applyCoachTheme()
  }, [])

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateScrollState()
    el.addEventListener("scroll", updateScrollState)
    return () => {
      el.removeEventListener("scroll", updateScrollState)
    }
  }, [updateScrollState])

  const handleClose = () => {
    setTheme(Theme.AthleteLight)
    void navigate(Path.Root)
  }

  const handleCta = () => {
    void navigate(Path.CreateCoach)
  }

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -280, behavior: "smooth" })
  }

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 280, behavior: "smooth" })
  }

  return (
    <div className="relative overflow-auto bg-surface-background">
      {/* Close button */}
      <div className="absolute top-0 right-0 z-1 mt-5 mr-5 flex flex-col items-end gap-2">
        <Button onClick={handleClose} variant="outline" size="iconMd">
          <X />
        </Button>
        <Text fontStyle="LS" className="hidden w-36 text-right text-textOnSurface-default sm:block">
          {word("coach.onboarding.intro.close_label")}
        </Text>
      </div>

      {/* Page content */}
      <div className="mx-auto flex w-full flex-col items-center gap-8 py-6">
        {/* Header */}
        <div className="flex flex-col items-center gap-4 text-center">
          {/* Logo */}
          <div className="flex gap-2">
            <LogoHustleUpSmall color="currentColor" />
            <LogoHustleUpText color="currentColor" />
          </div>

          <div className="flex flex-col items-center gap-0 px-4 text-center">
            {/* Heading */}
            <Text fontStyle="HL" className="font-bold text-textOnSurface-default">
              {currentCoach ? word("coach.onboarding.intro.title_pro") : word("coach.onboarding.intro.title")}
            </Text>

            {/* Subtitle */}
            <Text fontStyle="BL" className="text-textOnSurface-default">
              {currentCoach ? word("coach.onboarding.intro.subtitle_pro") : word("coach.onboarding.intro.subtitle")}
            </Text>

            {/* Badge */}
            <div className="mt-2 flex flex-col items-center gap-2 sm:flex-row">
              <Badge variant="neutralSubtle" size="lg">
                <Text fontStyle="LM" className="text-textOnSurface-default">
                  {word("coach.onboarding.intro.badge_desktop")}
                </Text>
              </Badge>
              {currentCoach && (
                <Badge variant="primarySubtle" size="lg">
                  <Text fontStyle="LM">{word("coach.onboarding.intro.badge_pro")}</Text>
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Feature cards — mobile carousel */}
        <div className="-mt-8 w-full sm:hidden">
          <Carousel opts={{ align: "start" }}>
            <CarouselIndicators>
              {FEATURE_CARDS.map((_, index) => (
                <CarouselIndicator key={index} index={index} />
              ))}
            </CarouselIndicators>
            <CarouselContent>
              {FEATURE_CARDS.map(({ key, icon }) => (
                <CarouselItem key={key} className="pr-6 pl-10">
                  <FeatureCard featureKey={key} icon={icon} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>

        {/* Feature cards — desktop horizontal scroll with arrows */}
        <div className="relative hidden w-full sm:block">
          <Button
            onClick={scrollLeft}
            disabled={!canScrollLeft}
            variant="outline"
            size="iconMd"
            className="absolute top-1/2 left-2 z-10 -translate-y-1/2 rounded-full shadow-sm transition-opacity disabled:opacity-0"
          >
            <ChevronLeftIcon className="mr-0.5 size-4" />
          </Button>
          <div ref={scrollRef} className="no-scrollbar flex w-full gap-4 overflow-x-auto px-10 pb-2">
            {FEATURE_CARDS.map(({ key, icon }) => (
              <FeatureCard key={key} featureKey={key} icon={icon} className="w-76 shrink-0" />
            ))}
          </div>
          <Button
            onClick={scrollRight}
            disabled={!canScrollRight}
            variant="outline"
            size="iconMd"
            className="absolute top-1/2 right-2 z-10 -translate-y-1/2 rounded-full shadow-sm transition-opacity disabled:opacity-0"
          >
            <ChevronRightIcon className="ml-0.5 size-4" />
          </Button>
        </div>

        {/* CTA */}
        <Button variant="primary" size="lg" onClick={handleCta}>
          {currentCoach ? word("coach.onboarding.intro.cta_pro") : word("coach.onboarding.intro.cta")}
        </Button>
      </div>
    </div>
  )
}

function FeatureCard({
  featureKey,
  icon,
  className,
}: {
  featureKey: FeatureKey
  icon: React.ReactNode
  className?: string
}) {
  const titleKey = word(`coach.onboarding.intro.feature.${featureKey}.title`)
  const bodyKey = word(`coach.onboarding.intro.feature.${featureKey}.body`)

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-2xl border border-surface-border-gray-muted bg-linear-to-b from-surface-background-secondary to-surface-background-default p-4",
        className,
      )}
    >
      <div className="flex flex-col">
        {icon}
        <Text fontStyle="TM">{titleKey}</Text>
      </div>
      <div className="h-40 w-full rounded-lg bg-black" />
      <Text fontStyle="BM" className="text-textOnSurface-default">
        {bodyKey}
      </Text>
    </div>
  )
}
